import { alpha, PaletteColorOptions } from '@mui/material/styles';

import { extraColor } from './extraColors';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

// SETUP COLORS

export const common = {
  black: '#000000',
  white: '#FFFFFF',
};

export const grey = {
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  '8': 'rgba(145, 158, 171, 0.08)',
  '12': 'rgba(145, 158, 171, 0.12)',
  '16': 'rgba(145, 158, 171, 0.16)',
  '20': 'rgba(145, 158, 171, 0.2)',
  '24': 'rgba(145, 158, 171, 0.24)',
  '32': 'rgba(145, 158, 171, 0.32)',
  '48': 'rgba(145, 158, 171, 0.48)',
  '56': 'rgba(145, 158, 171, 0.56)',
  '64': 'rgba(145, 158, 171, 0.64)',
  '72': 'rgba(145, 158, 171, 0.72)',
  '80': 'rgba(145, 158, 171, 0.8)',
};

export const primary = {
  lighter: '#FEE9D1',
  light: '#FDAB76',
  main: '#FA541C',
  dark: '#B3200E',
  darker: '#770508',
  contrastText: common.white,
  '8': 'rgba(250, 84, 28, 0.08)',
  '12': 'rgba(250, 84, 28, 0.12)',
  '16': 'rgba(250, 84, 28, 0.16)',
  '20': 'rgba(250, 84, 28, 0.2)',
  '24': 'rgba(250, 84, 28, 0.24)',
  '32': 'rgba(250, 84, 28, 0.32)',
  '48': 'rgba(250, 84, 28, 0.48)',
};

export const secondary = {
  lighter: '#EFD6FF',
  light: '#C684FF',
  main: '#8E33FF',
  dark: '#5119B7',
  darker: '#27097A',
  contrastText: common.white,
  '8': 'rgba(142, 51, 255, 0.08)',
  '12': 'rgba(142, 51, 255, 0.12)',
  '16': 'rgba(142, 51, 255, 0.16)',
  '20': 'rgba(142, 51, 255, 0.2)',
  '24': 'rgba(142, 51, 255, 0.24)',
  '32': 'rgba(142, 51, 255, 0.32)',
  '48': 'rgba(142, 51, 255, 0.48)',
};

export const info = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: common.white,
  '8': 'rgba(0, 184, 217, 0.08)',
  '12': 'rgba(0, 184, 217, 0.12)',
  '16': 'rgba(0, 184, 217, 0.16)',
  '20': 'rgba(0, 184, 217, 0.2)',
  '24': 'rgba(0, 184, 217, 0.24)',
  '32': 'rgba(0, 184, 217, 0.32)',
  '48': 'rgba(0, 184, 217, 0.48)',
};

export const success = {
  lighter: '#D3FCD2',
  light: '#77ED8B',
  main: '#22C55E',
  dark: '#118D57',
  darker: '#065E49',
  contrastText: common.white,
  '8': 'rgba(34, 197, 94, 0.08)',
  '12': 'rgba(34, 197, 94, 0.12)',
  '16': 'rgba(34, 197, 94, 0.16)',
  '20': 'rgba(34, 197, 94, 0.2)',
  '24': 'rgba(34, 197, 94, 0.24)',
  '32': 'rgba(34, 197, 94, 0.32)',
  '48': 'rgba(34, 197, 94, 0.48)',
};

export const warning = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: '#1C252E',
  '8': 'rgba(255, 171, 0, 0.08)',
  '12': 'rgba(255, 171, 0, 0.12)',
  '16': 'rgba(255, 171, 0, 0.16)',
  '20': 'rgba(255, 171, 0, 0.2)',
  '24': 'rgba(255, 171, 0, 0.24)',
  '32': 'rgba(255, 171, 0, 0.32)',
  '48': 'rgba(255, 171, 0, 0.48)',
};

export const error = {
  lighter: '#ffe4d6',
  light: '#ff9b83',
  main: '#ff3632',
  dark: '#b71931',
  darker: '#7a092e',
  contrastText: common.white,
  '8': 'rgba(255, 54, 50, 0.08)',
  '12': 'rgba(255, 54, 50, 0.12)',
  '16': 'rgba(255, 54, 50, 0.16)',
  '20': 'rgba(255, 54, 50, 0.2)',
  '24': 'rgba(255, 54, 50, 0.24)',
  '32': 'rgba(255, 54, 50, 0.32)',
  '48': 'rgba(255, 54, 50, 0.48)',
};

export const black = {
  main: common.black,
  '8': 'rgba(0, 0, 0, 0.08)',
  '12': 'rgba(0, 0, 0, 0.12)',
  '16': 'rgba(0, 0, 0, 0.16)',
  '20': 'rgba(0, 0, 0, 0.2)',
  '24': 'rgba(0, 0, 0, 0.24)',
  '32': 'rgba(0, 0, 0, 0.32)',
  '48': 'rgba(0, 0, 0, 0.48)',
  '56': 'rgba(0, 0, 0, 0.56)',
  '64': 'rgba(0, 0, 0, 0.64)',
  '72': 'rgba(0, 0, 0, 0.72)',
  '80': 'rgba(0, 0, 0, 0.8)',
};

export const blackSuccess = {
  lighter: '#d3fcd2',
  light: '#77ed8b',
  main: '#22c55e',
  dark: '#118d57',
  darker: '#065e49',
  contrastText: common.white,
  '8': 'rgba(34, 197, 94, 0.08)',
  '12': 'rgba(34, 197, 94, 0.12)',
  '16': 'rgba(34, 197, 94, 0.16)',
  '20': 'rgba(34, 197, 94, 0.2)',
  '24': 'rgba(34, 197, 94, 0.24)',
  '32': 'rgba(34, 197, 94, 0.32)',
  '48': 'rgba(34, 197, 94, 0.48)',
};

export const white = {
  main: common.white,
  '8': 'rgba(255, 255, 255, 0.08)',
  '12': 'rgba(255, 255, 255, 0.12)',
  '16': 'rgba(255, 255, 255, 0.16)',
  '20': 'rgba(255, 255, 255, 0.2)',
  '24': 'rgba(255, 255, 255, 0.24)',
  '32': 'rgba(255, 255, 255, 0.32)',
  '48': 'rgba(255, 255, 255, 0.48)',
  '56': 'rgba(255, 255, 255, 0.56)',
  '64': 'rgba(255, 255, 255, 0.64)',
  '72': 'rgba(255, 255, 255, 0.72)',
  '80': 'rgba(255, 255, 255, 0.8)',
};

export const action = {
  hover: alpha(grey[500], 0.08),
  selected: alpha(grey[500], 0.16),
  disabled: alpha(grey[500], 0.8),
  disabledBackground: alpha(grey[500], 0.24),
  focus: alpha(grey[500], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
};

const base = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  grey,
  common,
  divider: alpha(grey[500], 0.2),
  action,
  black,
  blackSuccess,
  white,
};

// ----------------------------------------------------------------------

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...base,
    mode: 'light',
    text: {
      primary: grey[800],
      secondary: grey[600],
      disabled: grey[500],
    },
    background: {
      paper: common.white,
      default: common.white,
      neutral: grey[200],
    },
    action: {
      ...base.action,
      active: grey[600],
    },
    extraColors: extraColor.light,
  };

  const dark = {
    ...base,
    mode: 'dark',
    text: {
      primary: common.white,
      secondary: grey[500],
      disabled: grey[600],
    },
    background: {
      paper: grey[800],
      default: grey[900],
      neutral: alpha(grey[500], 0.12),
    },
    action: {
      ...base.action,
      active: grey[500],
    },
    extraColors: extraColor.dark,
  };

  return mode === 'light' ? light : dark;
}

// palette에 특정 속성값 추가
declare module '@mui/material/styles' {
  interface PaletteColor {
    lighter: string;
    light: string;
    main: string;
    dark: string;
    darker: string;
    contrastText: string;
    '8': string;
    '12'?: string;
    '16'?: string;
    '20'?: string;
    '24'?: string;
    '32'?: string;
    '48'?: string;
  }

  interface Palette {
    primary: PaletteColor;
    secondary: PaletteColor;
    error: PaletteColor;
    warning: PaletteColor;
    info: PaletteColor;
    success: PaletteColor;
    black: typeof black;
    blackSuccess: typeof blackSuccess;
    white: typeof white;
  }

  interface PaletteOptions {
    primary?: PaletteColorOptions;
    secondary?: PaletteColorOptions;
    error?: PaletteColorOptions;
    warning?: PaletteColorOptions;
    info?: PaletteColorOptions;
    success?: PaletteColorOptions;
    black?: typeof black;
    blackSuccess?: typeof blackSuccess;
    white?: typeof white;
  }
}

declare module '@mui/material' {
  interface Color {
    '8'?: string;
    '12'?: string;
    '16'?: string;
    '20'?: string;
    '24'?: string;
    '32'?: string;
    '48'?: string;
    '56'?: string;
    '64'?: string;
    '72'?: string;
    '80'?: string;
  }
}
